import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { ReserveHeader } from "@/components/ReserveHeader";
import { Button } from "@/components/ui/button";
import { MfaEnrolmentCard } from "@/components/MfaEnrolmentCard";

import {
  listMyDevices,
  revokeDevice,
  revokeAllOtherDevices,
} from "@/lib/security-account.functions";
import { getDeviceFingerprint } from "@/lib/device-fingerprint";

export const Route = createFileRoute("/_authenticated/account/security")({
  head: () => ({
    meta: [
      { title: "Security & Devices — The Hospitality Reserve" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: SecurityPage,
});

type Device = {
  id: string;
  device_fingerprint: string;
  browser: string | null;
  os: string | null;
  ip_address: string | null;
  first_seen_at: string;
  last_seen_at: string;
  revoked_at: string | null;
};

function SecurityPage() {
  const list = useServerFn(listMyDevices);
  const revoke = useServerFn(revokeDevice);
  const revokeAll = useServerFn(revokeAllOtherDevices);
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);
  const [fp, setFp] = useState("");

  useEffect(() => {
    setFp(getDeviceFingerprint());
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function refresh() {
    setLoading(true);
    const res = await list({});
    setDevices((res.devices ?? []) as Device[]);
    setLoading(false);
  }

  async function onRevoke(id: string) {
    const res = await revoke({ data: { deviceId: id } });
    if ("error" in res && res.error) return toast.error(res.error);
    toast.success("Device revoked from your list.");
    void refresh();
  }

  async function onRevokeAll() {
    if (!confirm("Sign out of every other device? You will stay signed in here.")) return;
    const res = await revokeAll({ data: { currentFingerprint: fp } });
    if ("error" in res && res.error) return toast.error(res.error);
    toast.success("Signed out of other devices.");
    void refresh();
  }

  return (
    <div className="min-h-screen bg-background">
      <ReserveHeader />
      <main className="mx-auto max-w-3xl px-4 py-10 space-y-6">
        <div>
          <Link to="/account" className="text-sm text-muted-foreground hover:underline">
            ← Account
          </Link>
          <h1 className="text-2xl font-semibold mt-2">Security &amp; devices</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Devices that have signed in to your account. If you see anything you don't
            recognise, revoke it and change your password.
          </p>
        </div>
        <MfaEnrolmentCard />


        <div className="flex items-center justify-between rounded-lg border bg-card p-4">
          <div>
            <p className="text-sm font-medium">Sign out of other devices</p>
            <p className="text-xs text-muted-foreground">
              Keeps this device signed in and immediately signs out all others.
            </p>
          </div>
          <Button variant="destructive" onClick={onRevokeAll}>
            Sign out others
          </Button>
        </div>

        <ul className="divide-y rounded-lg border bg-card">
          {loading ? (
            <li className="p-6 text-sm text-muted-foreground">Loading devices…</li>
          ) : devices.length === 0 ? (
            <li className="p-6 text-sm text-muted-foreground">No devices recorded yet.</li>
          ) : (
            devices.map((d) => {
              const isCurrent = d.device_fingerprint === fp;
              return (
                <li key={d.id} className="flex items-center justify-between gap-4 p-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium truncate">
                        {d.browser ?? "Browser"} on {d.os ?? "device"}
                      </p>
                      {isCurrent ? (
                        <span className="text-xs rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 px-2 py-0.5">
                          This device
                        </span>
                      ) : null}
                      {d.revoked_at ? (
                        <span className="text-xs rounded-full bg-muted text-muted-foreground px-2 py-0.5">
                          Revoked
                        </span>
                      ) : null}
                    </div>
                    <p className="text-xs text-muted-foreground truncate">
                      {d.ip_address ?? "IP unknown"} · last seen{" "}
                      {new Date(d.last_seen_at).toLocaleString()}
                    </p>
                  </div>
                  {!isCurrent && !d.revoked_at ? (
                    <Button variant="outline" size="sm" onClick={() => onRevoke(d.id)}>
                      Revoke
                    </Button>
                  ) : null}
                </li>
              );
            })
          )}
        </ul>
      </main>
    </div>
  );
}
